// @flow
import * as React from 'react';
import styled from 'styled-components';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash.debounce';
import googleMaps from 'utils/googleMaps';
import * as localStorage from 'utils/localStorage';
import Box from 'components/Box';
import Img from 'components/Image';
import { ClearButton } from 'components/Button';
import LocationIcon from 'location.svg';
import { TooltipStandalone } from 'components/TooltipStandalone';
import Input from './Input';
import Text from './Text';
import { colors, fontWeights, transition } from '../theme';

const testMode = localStorage.getItem('testMode');

const { useState, useEffect, useMemo } = React;

const getCity = (addressComponents: AddressComponents) =>
  addressComponents.locality ||
  addressComponents.postal_town ||
  addressComponents.sublocality_level_1 ||
  addressComponents.administrative_area_level_3 ||
  addressComponents.administrative_area_level_2;

const StyledLocationIcon = styled(LocationIcon)`
  position: absolute;
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
  left: 16px;
`;

const InputContainer = styled.div`
  display: inline-flex;
  position: relative;
  align-items: center;
  width: 100%;
`;

const AutosuggestWrapper = styled.div`
  position: relative;

  .react-autosuggest {
    &__suggestions-container {
      display: none;

      &--open {
        display: block;
        margin-top: 4px;
        position: absolute;
        width: 100%;
        overflow-y: scroll;
        max-height: 400px;
        border: 1px solid;
        border-color: ${colors.silver};
        background-color: #fff;
        font-size: 18px;
        line-height: 19px;
        border-radius: 4px;
        z-index: 2;
      }
    }

    &__suggestions-list {
      margin: 0;
      padding: 0 ${({ borderSize }) => borderSize}px;
      list-style-type: none;
    }

    &__suggestion {
      cursor: pointer;
      padding: ${props => (props.large ? '16px 24px' : '10px 1em')};
      transition: all ${transition};
      ${({ borderSize }) => `
        box-shadow: 0 -${borderSize}px rgba(255,255,255,0) inset, 0 ${borderSize}px rgba(255,255,255,0) inset;
      `}

      &:not(:first-child) {
        border-top: 1px dashed ${colors.aluminum};
      }

      &--highlighted {
        background-color: ${colors.offWhite};
        ${({ borderSize }) => `
          box-shadow: 0 -${borderSize}px white inset, 0 ${borderSize}px white inset;
        `}
      }
    }
  }
`;

const StyledInput = React.forwardRef<any, InputContainer>(
  (
    {
      showLocationIcon,
      onClear,
      className,
      gMapsErrored,
      setGMapsError,
      ...inputProps
    }: {
      showLocationIcon: boolean,
      onClear: ?() => void,
      className?: string,
      gMapsErrored: boolean,
      setGMapsError: Function,
      'data-qa-id': string
    },
    ref
  ) => (
    <InputContainer className={className}>
      {showLocationIcon && <StyledLocationIcon width="15" height="21" />}
      <Input
        {...inputProps}
        ref={ref}
        height="100%"
        width="100%"
        pl={showLocationIcon ? '38px' : '18px'}
        pr={onClear ? '38px' : '18px'}
      />
      {onClear && (
        <ClearButton
          onClick={onClear}
          data-qa-id={`${inputProps['data-qa-id']}-clear`}
        />
      )}
      {gMapsErrored && (
        <TooltipStandalone>
          {getText(
            'Our geolocation provider (Google Maps) did not load correctly. Please reload the page.'
          )}
        </TooltipStandalone>
      )}
    </InputContainer>
  )
);

type Props = {
  hasError?: boolean,
  onLocationChange: SearchLocation => void,
  onLocationInputValueChange?: (InputEvent, { newValue: string }) => void,
  hasSuccess?: boolean,
  clearLocation: () => void,
  locationInputValue: string,
  'data-qa-id'?: string,
  placeholder: ?string,
  showLocationIcon?: boolean,
  showClearButton: boolean,
  className?: string,
  setGMapsError: Function,
  includeCity?: boolean,
  includeFull?: boolean,
  onBlur?: Function,
  disabled?: boolean,
  highlightFirstSuggestion?: boolean
};

// eslint-disable-next-line react/no-multi-comp
const LocationInput = ({
  hasError,
  hasSuccess,
  onLocationChange,
  onLocationInputValueChange,
  clearLocation,
  locationInputValue,
  placeholder,
  showLocationIcon = false,
  showClearButton,
  className,
  setGMapsError,
  includeCity,
  includeFull,
  disabled,
  highlightFirstSuggestion,
  onBlur,
  ...props
}: Props) => {
  const [autocompleteService, setAutocompleteService] = useState();
  const [geocoder, setGeocoder] = useState();
  const [sessionToken, setSessionToken] = useState();

  const handlePlaceDetails = place => {
    const addressComponents = place.address_components.reduce(
      (hash, component) => {
        const attributeName = component.types[0];
        hash[attributeName] = [
          'country',
          'administrative_area_level_1'
        ].includes(attributeName)
          ? component.short_name
          : component.long_name;
        return hash;
      },
      {}
    );

    if (!('country' in addressComponents)) {
      // below: special handling of 'south east asia', 'gaza', etc.
      // i.e. results for which the autocomplete api does not return a country code
      // we will use the iso 3166 user-defined 'XA' code for this situation
      // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#User-assigned_code_elements

      addressComponents.country = 'XA';
      place.types[0] = 'undefined_country_geocodes';
    }

    const countryCode = addressComponents.country;
    const stateCode = addressComponents.administrative_area_level_1;

    let city;
    let full;

    if (includeCity) {
      city = getCity(addressComponents);
    }

    if (includeFull) {
      full = place.formatted_address;
    }

    const boundingBox = place.geometry.viewport.toJSON();

    onLocationChange({
      text: place.formatted_address,
      stateCode,
      countryCode,
      boundingBoxN: boundingBox.north,
      boundingBoxW: boundingBox.west,
      boundingBoxS: boundingBox.south,
      boundingBoxE: boundingBox.east,
      geoType: place.types[0],
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      full,
      city
    });
  };

  const onSuggestionSelected = (e, { suggestion }) => {
    if (!geocoder) return;

    return geocoder.geocode(
      {
        placeId: suggestion.place_id
      },
      results => handlePlaceDetails(results[0])
    );
  };

  const [suggestions, updateSuggestions] = useState<
    $ReadOnlyArray<{ value: string, name: string }>
  >([]);

  const [inProgressQuery, updateInProgressQuery] = useState<?Promise<
    $ReadOnlyArray<{ name: string, value: string }>
  >>(null);

  useEffect(
    () => {
      if (!autocompleteService) {
        googleMaps()
          .then(maps => {
            setAutocompleteService(new maps.places.AutocompleteService());
            setGeocoder(new maps.Geocoder());
            setSessionToken(new google.maps.places.AutocompleteSessionToken());
          })
          .catch(error => {
            setGMapsError(error.message);
          });
      }
    },
    [autocompleteService, setGMapsError]
  );

  const getPlacePredictions = input =>
    new Promise((resolve, reject) => {
      if (!autocompleteService) {
        return reject();
      }
      autocompleteService.getPlacePredictions(
        {
          input,
          types: ['geocode'],
          sessionToken
        },
        (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            resolve(results);
          } else if (
            status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            resolve([]);
          } else {
            reject(new Error(status));
          }
        }
      );
    });

  const loadSuggestions = (value: string) => {
    // Cancel the previous request
    if (inProgressQuery) {
      inProgressQuery.then(() => {
        debounce(() => loadSuggestions(value));
      });
    } else {
      const query = getPlacePredictions(value);

      query.then(options => {
        updateSuggestions(options);
        updateInProgressQuery(null);
      });
      updateInProgressQuery(query);
      return query;
    }
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    if (autocompleteService) {
      loadSuggestions(value);
    }
  };

  const renderSuggestion = useMemo(
    () => suggestion => (
      <Text
        className="idlst-google-suggestion"
        fontWeight={fontWeights.semiBold}
      >
        {suggestion.description}
      </Text>
    ),
    []
  );

  const onSuggestionsClearRequested = () => {
    updateSuggestions([]);
    updateInProgressQuery(null);
  };

  const renderSuggestionsContainer = ({
    containerProps,
    children
  }: {
    containerProps: Object,
    children: React.Node
  }) => (
    <div {...containerProps}>
      {children}
      <Box display="flex" justifyContent="flex-end">
        <Box
          component={Img}
          width={150}
          layout="responsive"
          src="/static/images/poweredbygoogle/powered_by_google_on_white.png"
          srcSet="/static/images/poweredbygoogle/powered_by_google_on_white.png, /static/images/poweredbygoogle/powered_by_google_on_white@2x.png 2x, /static/images/poweredbygoogle/powered_by_google_on_white@3x.png 3x"
          alt="Powered by Google"
          alwaysShow
        />
      </Box>
    </div>
  );

  const onKeyPress = (event: SyntheticKeyboardEvent<>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const getSuggestionValue = suggestion => suggestion.description;

  const inputProps = {
    className,
    disabled,
    hasSuccess,
    onChange: onLocationInputValueChange,
    onBlur,
    onKeyPress,
    onClear: showClearButton || testMode ? clearLocation : null,
    placeholder,
    setGMapsError,
    showLocationIcon,
    value: locationInputValue || '',
    'aria-label': getText('Location'),
    'data-qa-id': props['data-qa-id'] || 'location-input'
  };

  return (
    <AutosuggestWrapper large borderSize={4}>
      <Autosuggest
        highlightFirstSuggestion={highlightFirstSuggestion}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={v => v && v.toString().trim().length >= 2}
        renderInputComponent={renderInputComponentProps => (
          <StyledInput {...renderInputComponentProps} />
        )}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={inputProps}
      />
    </AutosuggestWrapper>
  );
};

export default LocationInput;
